/**
 * TemplatePicker — reusable reply-template popover
 * ------------------------------------------------
 * Used by:
 *   • CRM Chats composer (message reply)
 *   • CRM Tickets edit modal (in-modal reply pane)
 *
 * Lazy-loads templates the first time it's opened, filters by tag chip
 * or free-text search, and calls `onPick(template)` with the chosen
 * row so the caller can substitute personalisation tokens.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChatText } from "@phosphor-icons/react";
import { api } from "../lib/api";

export function TemplatePicker({ onPick, align = "right" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [tpls, tg] = await Promise.all([
        api.get(`/reply-templates${tag ? `?tag=${encodeURIComponent(tag)}` : ""}`),
        api.get("/reply-templates/tags"),
      ]);
      setItems(tpls.data);
      setTags(tg.data);
    } catch { setItems([]); }
  }, [tag]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter(t =>
      t.title.toLowerCase().includes(qq) ||
      t.body.toLowerCase().includes(qq) ||
      (t.tags || []).some(tg => tg.includes(qq))
    );
  }, [items, q]);

  return (
    <div className="relative shrink-0">
      <button type="button"
              onClick={() => setOpen(v => !v)}
              data-testid="chat-templates-btn"
              title="Insert reply template"
              className="h-10 w-10 rounded-full bg-white hover:bg-[#F0F7EE] border border-[#D4E0D1] text-[#2D5A27] flex items-center justify-center">
        <ChatText size={16} weight="bold"/>
      </button>
      {open && (
        <div className={`absolute ${align === "left" ? "left-0" : "right-0"} bottom-12 z-40 w-80 bg-white border border-[#E5E2D9] rounded-lg shadow-2xl overflow-hidden`}
             onMouseLeave={() => setOpen(false)}
             data-testid="chat-templates-popover">
          <div className="p-2 border-b border-[#F0EEE6]">
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search templates…"
                    data-testid="chat-templates-search"
                    className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-xs"/>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <button onClick={() => setTag("")}
                        data-testid="chat-templates-tag-all"
                        className={`h-6 px-2 rounded-full text-[10px] font-medium border ${
                          tag === "" ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                     : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                        }`}>all</button>
                {tags.map(t => (
                  <button key={t.tag}
                          data-testid={`chat-templates-tag-${t.tag}`}
                          onClick={() => setTag(t.tag)}
                          className={`h-6 px-2 rounded-full text-[10px] font-medium border ${
                            tag === t.tag ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                          : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                          }`}>{t.tag}</button>
                ))}
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-[#F0EEE6]"
               data-testid="chat-templates-list">
            {items === null && (
              <div className="p-4 text-center text-[11px] text-[#8A8780]">Loading…</div>
            )}
            {items && filtered.length === 0 && (
              <div className="p-4 text-center text-[11px] text-[#8A8780]">
                No templates yet.{" "}
                <a href="/settings/reply-templates" className="underline text-[#2D5A27]">Add some →</a>
              </div>
            )}
            {filtered.map(t => (
              <button key={t.id}
                      type="button"
                      data-testid={`chat-template-pick-${t.id}`}
                      onClick={() => { onPick(t); setOpen(false); }}
                      className="block w-full text-left px-3 py-2 hover:bg-[#F0F7EE]">
                <div className="text-sm font-medium text-[#1A1A1A]">{t.title}</div>
                <div className="text-[11px] text-[#5C5A55] line-clamp-2 mt-0.5 whitespace-pre-wrap">{t.body}</div>
                {t.tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {t.tags.slice(0, 4).map(tg => (
                      <span key={tg} className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 rounded bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC]">{tg}</span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
